import { useMultimediaPreview } from "@mirage/mosaics/SearchPage/Multimedia/hooks/useMultimediaPreview";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { SearchResultIcon } from "@mirage/shared/icons";
import EventIcon from "@mirage/shared/icons/EventIcon";
import { FileContentIcon } from "@mirage/shared/icons/FileContentIcon";
import { RESULT_BADGE_ICON_SIZE } from "@mirage/shared/icons/util";
import { utcToZonedTime } from "date-fns-tz";
import { PersonIcon } from "./PersonIcon";

import type { ContentIconProps } from "@dropbox/dash-component-library";
import { Box } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Hover_DashMultimedia } from "@mirage/analytics/events/types/hover_dash_multimedia";
import type { SearchResult } from "@mirage/service-dbx-api";
import { getMessageSenderString } from "@mirage/service-dbx-api/service/utils";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { useCallback, useMemo } from "react";
import { getAppDisplayNameFromResult } from "./util/resultUtil";

type Props = {
  result: SearchResult;
  size?: ContentIconProps["size"];
};

export default function ResultIcon({ result, size = "medium" }: Props) {
  const shouldFetchMultimediaResults = useFeatureFlagValue(
    "dash_2025_03_13_multimedia_search",
  );
  const { base64Image, base64Loading, hasError } = useMultimediaPreview(
    shouldFetchMultimediaResults ? result.previewUrl || undefined : undefined,
    512,
  );
  const thumbnailUrl =
    shouldFetchMultimediaResults === "V1" &&
    base64Image &&
    !base64Loading &&
    !hasError
      ? base64Image
      : undefined;

  const { reportPapEvent, searchSessionManager } = useMirageAnalyticsContext();
  const logMultimediaIconHover = useCallback(() => {
    if (thumbnailUrl) {
      reportPapEvent(
        PAP_Hover_DashMultimedia({
          searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
        }),
      );
    }
  }, [thumbnailUrl, reportPapEvent, searchSessionManager]);
  const isEvent = result?.recordType?.[".tag"] === "event" && result?.startTime;
  const appName = useMemo(() => getAppDisplayNameFromResult(result), [result]);

  const icon = useMemo(() => {
    if (isEvent) {
      let eventDate = new Date(result.startTime as number); // Cannot be null if isEvent is true
      if (result?.isAllDay) {
        // All-day events are represented as UTC, so we need to convert to local time
        // TODO: Temporary fix until https://jira.dropboxer.net/browse/OTCIP-46 is
        // resolved and the API provides just the date for these all-day events.
        eventDate = utcToZonedTime(eventDate, "UTC");
      }
      // checking if the date is valid here prevents EventIcon from throwing on invalid dates
      if (eventDate instanceof Date && !isNaN(eventDate.getTime())) {
        return (
          <EventIcon
            size={size}
            eventDate={eventDate}
            sourceIcon={
              <DigTooltip title={appName} placement="top">
                <Box as="span" pointerEvents="auto">
                  <SearchResultIcon
                    result={result}
                    size={RESULT_BADGE_ICON_SIZE}
                  />
                </Box>
              </DigTooltip>
            }
          />
        );
      }
    }

    const sender = result?.sender ?? result?.creator;
    if (
      (result.fileTypeInfo?.id === "email" ||
        result.fileTypeInfo?.id === "chat") &&
      sender &&
      getMessageSenderString(sender)
    ) {
      return (
        <PersonIcon
          result={result}
          person={sender}
          size={size}
          badgeTooltipTitle={appName}
        />
      );
    }

    return (
      <FileContentIcon
        thumbnailUrl={
          shouldFetchMultimediaResults === "V1" ? thumbnailUrl : undefined
        }
        onThumbnailHover={
          shouldFetchMultimediaResults === "V1" && thumbnailUrl
            ? logMultimediaIconHover
            : undefined
        }
        content={result}
        size={size}
        badgeTooltipTitle={appName}
      />
    );
  }, [
    isEvent,
    result,
    shouldFetchMultimediaResults,
    thumbnailUrl,
    logMultimediaIconHover,
    size,
    appName,
  ]);

  return icon;
}
